<template>
  <div
    class="video-tile"
    :class="{
      'video-tile--speaking': isSpeaking,
      'video-tile--screen-share': isScreenShare,
      'video-tile--video-off': !hasVideo,
    }"
  >
    <!-- Video element -->
    <video
      v-show="hasVideo"
      ref="videoEl"
      class="video-tile__video"
      :class="{ 'video-tile__video--mirrored': isLocal && !isScreenShare }"
      autoplay
      playsinline
      :muted="isLocal"
    />

    <!-- Avatar fallback when camera is off -->
    <div v-if="!hasVideo" class="video-tile__avatar">
      <div class="video-tile__avatar-initials">{{ initials }}</div>
    </div>

    <!-- Pin button — visible on hover for remote participants -->
    <button
      v-if="showPinButton && !isLocal"
      class="video-tile__pin-btn"
      :class="{ 'video-tile__pin-btn--active': isPinned }"
      @click.stop="$emit('pin')"
    >
      <v-icon size="14">{{ isPinned ? 'mdi-pin-off' : 'mdi-pin' }}</v-icon>
    </button>

    <!-- Bottom name bar -->
    <div class="video-tile__name-bar">
      <v-icon v-if="isMuted" size="14" color="error" class="mr-1">mdi-microphone-off</v-icon>
      <span class="video-tile__name">{{ displayName }}</span>
      <v-chip v-if="isAdmin" size="x-small" color="amber" class="ml-1" label>Host</v-chip>
      <v-icon v-if="isScreenShare" size="14" color="blue-lighten-2" class="ml-1">mdi-monitor-share</v-icon>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VideoTile',

  props: {
    stream: {
      type: Object, // MediaStream (markRaw)
      default: null,
    },
    name: {
      type: String,
      default: 'Participant',
    },
    isMuted: {
      type: Boolean,
      default: false,
    },
    isVideoOff: {
      type: Boolean,
      default: false,
    },
    isSpeaking: {
      type: Boolean,
      default: false,
    },
    isLocal: {
      type: Boolean,
      default: false,
    },
    isScreenShare: {
      type: Boolean,
      default: false,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
    isPinned: {
      type: Boolean,
      default: false,
    },
    showPinButton: {
      type: Boolean,
      default: false,
    },
    sinkId: {
      type: String,
      default: null,
    },
  },

  emits: ['pin', 'sink-id-error'],

  computed: {
    hasVideo() {
      if (this.isVideoOff) return false;
      if (!this.stream) return false;
      return this.stream.getVideoTracks().some((t) => t.enabled && t.readyState === 'live');
    },

    displayName() {
      return this.isLocal ? `${this.name} (You)` : this.name;
    },

    initials() {
      return this.name
        .split(' ')
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase() ?? '')
        .join('');
    },
  },

  watch: {
    stream(newStream) {
      this.$nextTick(() => this.attachStream(newStream));
    },

    sinkId(newSinkId) {
      this.applySinkId(newSinkId);
    },
  },

  mounted() {
    this.attachStream(this.stream);
  },

  methods: {
    attachStream(stream) {
      const el = this.$refs.videoEl;
      if (!el) return;
      if (el.srcObject !== stream) {
        el.srcObject = stream ?? null;
      }
      if (stream) {
        el.play().catch(() => {});
      }
      this.applySinkId(this.sinkId);
    },

    // Local tile is always rendered muted, so choosing an output device is
    // meaningless for it — only remote/screen-share tiles actually play audio.
    applySinkId(sinkId) {
      const el = this.$refs.videoEl;
      if (!el || !sinkId || this.isLocal || typeof el.setSinkId !== 'function') return;
      el.setSinkId(sinkId).catch((e) => {
        console.warn('Could not switch speaker device', e);
        this.$emit('sink-id-error', sinkId);
      });
    },
  },
};
</script>

<style scoped>
.video-tile {
  position: relative;
  background: #2a2a2a;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: box-shadow 0.2s ease;
  min-height: 0;
}

.video-tile--speaking {
  box-shadow: 0 0 0 3px #00c853;
}

.video-tile__video {
  width: 100%;
  height: 100%;
  display: block;
}

.video-tile__video--mirrored {
  transform: scaleX(-1);
}

.video-tile__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: #3a3a3a;
}

.video-tile__avatar-initials {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #5c6bc0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 600;
  color: #fff;
  user-select: none;
}

.video-tile__name-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 6px 10px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  display: flex;
  align-items: center;
  font-size: 0.78rem;
  color: #fff;
  gap: 2px;
}

.video-tile__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.video-tile--screen-share .video-tile__video {
  object-fit: contain;
  background: #111;
}

.video-tile__pin-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.55);
  border: none;
  border-radius: 4px;
  color: #fff;
  cursor: pointer;
  padding: 4px 6px;
  opacity: 0;
  transition: opacity 0.15s ease;
  z-index: 2;
  display: flex;
  align-items: center;
}

.video-tile:hover .video-tile__pin-btn {
  opacity: 1;
}

.video-tile__pin-btn--active {
  background: #1976d2;
  opacity: 1;
}
</style>
